/**
 * Apariencia.tsx — El usuario elige el tema de la app (ruta /menu/apariencia).
 *
 * Tres opciones en tarjetas grandes y tocables: Claro, Oscuro y Automático.
 * Cada tarjeta lleva una miniatura que muestra CÓMO SE VE ese tema, para que
 * no haya que probarlos uno por uno. La elección la guarda useTema() en
 * localStorage y se aplica al instante.
 *
 * Visible para todo el mundo: clientes y administradores por igual.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSun, FaMoon, FaAdjust, FaCheck } from 'react-icons/fa';
import AppNavbar from '../components/AppNavbar';
import BottomNav from '../components/BottomNav';
import { useTema } from '../hooks/useTema';
import type { OpcionTema } from '../hooks/useTema';

const BackIcon  = FaArrowLeft as unknown as React.FC;
const SolIcon   = FaSun       as unknown as React.FC;
const LunaIcon  = FaMoon      as unknown as React.FC;
const AutoIcon  = FaAdjust    as unknown as React.FC;
const CheckIcon = FaCheck     as unknown as React.FC;

/* ---------------------------------------------------------------------------
   PALETAS DE LA MINIATURA

   Excepción deliberada a "todo color sale de var(--sn-*)": una miniatura tiene
   que dibujar un tema que NO es el activo (mostrar el oscuro estando en claro),
   y las variables siempre resuelven al tema activo. Estos valores son copia
   literal de src/styles/temas.css; si cambian allí, cambiarlos aquí.
--------------------------------------------------------------------------- */
type Paleta = {
  fondo: string;        // --sn-fondo
  superficie: string;   // --sn-superficie
  borde: string;        // --sn-borde
  lineaFuerte: string;  // simula un título (--sn-texto)
  linea: string;        // simula texto secundario
  acento: string;       // botón de acción (--sn-accion)
};

const PALETA_CLARA: Paleta = {
  fondo: '#f3f5f6',
  superficie: '#ffffff',
  borde: '#dde1e4',
  lineaFuerte: '#202930',
  linea: '#c9ced3',
  acento: '#a41619',
};

const PALETA_OSCURA: Paleta = {
  fondo: '#12171b',
  superficie: '#1a2126',
  borde: '#323c44',
  lineaFuerte: '#e9edf0',
  linea: '#4a555e',
  acento: '#cf3035',
};

/**
 * Capa que ocupa toda la miniatura. Se escribe con las cuatro esquinas en vez
 * de `inset: 0` porque el atajo no existe en WebViews de Android antiguas.
 */
const CAPA_COMPLETA = { position: 'absolute' as const, top: 0, right: 0, bottom: 0, left: 0 };

/** Maqueta diminuta de una pantalla de la app pintada con una paleta concreta. */
const Maqueta: React.FC<{ paleta: Paleta }> = ({ paleta }) => (
  <div style={{ ...CAPA_COMPLETA, background: paleta.fondo, display: 'flex', flexDirection: 'column' }}>
    {/* Barra superior con el punto de marca */}
    <div
      style={{
        height: 15, flexShrink: 0, background: paleta.superficie,
        borderBottom: `1px solid ${paleta.borde}`,
        display: 'flex', alignItems: 'center', gap: 4, padding: '0 5px',
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: paleta.acento, flexShrink: 0 }} />
      <span style={{ width: 24, height: 3, borderRadius: 2, background: paleta.lineaFuerte }} />
    </div>

    {/* Tarjeta de contenido */}
    <div style={{ flex: 1, padding: 6 }}>
      <div
        style={{
          height: '100%', background: paleta.superficie, border: `1px solid ${paleta.borde}`,
          borderRadius: 6, padding: 6, display: 'flex', flexDirection: 'column', gap: 4,
        }}
      >
        <span style={{ width: '55%', height: 4, borderRadius: 2, background: paleta.lineaFuerte }} />
        <span style={{ width: '85%', height: 3, borderRadius: 2, background: paleta.linea }} />
        <span style={{ width: '70%', height: 3, borderRadius: 2, background: paleta.linea }} />
        <span style={{ width: 30, height: 8, borderRadius: 4, background: paleta.acento, marginTop: 'auto' }} />
      </div>
    </div>
  </div>
);

/**
 * Miniatura de la tarjeta.
 * 'automatico' dibuja las dos maquetas partidas en diagonal, que es
 * exactamente lo que hace la opción: mitad y mitad según el teléfono.
 */
const Miniatura: React.FC<{ opcion: OpcionTema }> = ({ opcion }) => (
  <div
    aria-hidden="true"
    style={{
      position: 'relative', width: 92, height: 66, flexShrink: 0,
      borderRadius: 10, overflow: 'hidden', border: '1px solid var(--sn-borde)',
    }}
  >
    <Maqueta paleta={opcion === 'oscuro' ? PALETA_OSCURA : PALETA_CLARA} />

    {opcion === 'automatico' && (
      <>
        {/* Triángulo inferior derecho: el mismo diseño en tema oscuro */}
        <div style={{ ...CAPA_COMPLETA, clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}>
          <Maqueta paleta={PALETA_OSCURA} />
        </div>
        {/* Filo de la diagonal, para que el corte se lea claro */}
        <div
          style={{
            ...CAPA_COMPLETA,
            // 'to bottom right' hace que la franja del 50% caiga justo sobre la
            // recta esquina-superior-derecha → esquina-inferior-izquierda, que
            // es exactamente el borde del clipPath de arriba.
            background: 'linear-gradient(to bottom right, transparent calc(50% - 1px), var(--sn-borde-fuerte) calc(50% - 1px), var(--sn-borde-fuerte) calc(50% + 1px), transparent calc(50% + 1px))',
          }}
        />
      </>
    )}
  </div>
);

type DefinicionOpcion = {
  valor: OpcionTema;
  titulo: string;
  descripcion: string;
  icono: React.FC;
};

const OPCIONES: DefinicionOpcion[] = [
  { valor: 'claro',      titulo: 'Claro',      descripcion: 'Fondo luminoso. Se lee mejor con luz de día o en la calle.', icono: SolIcon },
  { valor: 'oscuro',     titulo: 'Oscuro',     descripcion: 'Grafito profundo. Descansa la vista de noche y gasta menos batería.', icono: LunaIcon },
  { valor: 'automatico', titulo: 'Automático', descripcion: 'Sigue la configuración de tu teléfono y cambia solo.', icono: AutoIcon },
];

const Apariencia: React.FC = () => {
  const navigate = useNavigate();
  const { opcion, temaDelSistema, cambiarTema } = useTema();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <AppNavbar />

      <div style={{ flex: 1, overflowY: 'auto' as const }}>
        {/* Cabecera de la pantalla, fija mientras se hace scroll */}
        <div
          style={{
            background: 'var(--sn-superficie)', padding: '12px 16px', display: 'flex', alignItems: 'center',
            gap: 12, borderBottom: '1px solid var(--sn-borde)', position: 'sticky', top: 0, zIndex: 10,
          }}
        >
          <button
            onClick={() => navigate(-1)}
            aria-label="Volver"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--sn-accion)' }}
          >
            <BackIcon />
          </button>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--sn-texto)' }}>Apariencia</div>
        </div>

        <div style={{ padding: 16, maxWidth: 500, margin: '0 auto' }}>
          <p style={{ fontSize: 12, color: 'var(--sn-texto-3)', marginBottom: 14 }}>
            Elige cómo quieres ver la app. El cambio se aplica al instante y se recuerda en este dispositivo.
          </p>

          <div role="radiogroup" aria-label="Tema de la aplicación" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {OPCIONES.map(({ valor, titulo, descripcion, icono }) => {
              const Icono = icono;
              const activa = opcion === valor;

              return (
                <button
                  key={valor}
                  type="button"
                  role="radio"
                  aria-checked={activa}
                  onClick={() => cambiarTema(valor)}
                  style={{
                    width: '100%', textAlign: 'left', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 12, padding: 12,
                    background: 'var(--sn-superficie)',
                    // Siempre 2px para que al seleccionar no se mueva el diseño
                    border: `2px solid ${activa ? 'var(--sn-marca)' : 'var(--sn-borde)'}`,
                    borderRadius: 14,
                    boxShadow: activa ? 'var(--sn-sombra)' : 'none',
                  }}
                >
                  <Miniatura opcion={valor} />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ fontSize: 13, color: activa ? 'var(--sn-marca)' : 'var(--sn-texto-2)', display: 'flex' }}>
                        <Icono />
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--sn-texto)' }}>{titulo}</span>
                    </div>

                    <div style={{ fontSize: 11, color: 'var(--sn-texto-3)', marginTop: 3, lineHeight: 1.35 }}>
                      {descripcion}
                    </div>

                    {valor === 'automatico' && activa && (
                      <div style={{ fontSize: 11, color: 'var(--sn-texto-2)', marginTop: 5, fontWeight: 600 }}>
                        Ahora mismo tu teléfono está en {temaDelSistema}.
                      </div>
                    )}
                  </div>

                  {/* Marca de la opción activa */}
                  <span
                    aria-hidden="true"
                    style={{
                      flexShrink: 0, width: 24, height: 24, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
                      background: activa ? 'var(--sn-accion)' : 'transparent',
                      color: activa ? 'var(--sn-accion-texto)' : 'transparent',
                      border: activa ? 'none' : '2px solid var(--sn-borde-fuerte)',
                    }}
                  >
                    <CheckIcon />
                  </span>
                </button>
              );
            })}
          </div>

          <p style={{ fontSize: 11, color: 'var(--sn-texto-3)', marginTop: 16, lineHeight: 1.4 }}>
            Tu elección se guarda solo en este teléfono. Si entras desde otro equipo, tendrás que elegirla otra vez.
          </p>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Apariencia;
